import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SearchIcon } from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  searchCandidateDistanceOptions,
  searchYearsOfExperienceOptions,
} from "@/lib/constants";
import { jobsStore } from "@/stores";
import { JobStatus, SearchParameters } from "@/types";

const searchWithAJobSchema = z.object({
  jobId: z.string(),
  radius: z.string(),
  yoe: z.string(),
});

const SearchWithAJobLocationSpecialInput = () => {
  const jobId = useWatch<{ jobId: string }>({ name: "jobId" });
  const jobs = jobsStore((state) => state.jobs);
  // We don't need to filter down to active jobs here because
  // the `jobId` is selected from a list of active jobs.
  const job = jobs.find(({ job }) => job.id === jobId);

  return (
    <FormItem className="flex flex-col text-slate-700">
      <FormLabel>Location</FormLabel>
      <span className="text-sm font-medium text-slate-500">
        {job?.job.city}, {job?.job.state}
      </span>
    </FormItem>
  );
};

const SearchWithAJobForm = ({
  closeDialog,
  searchParameters,
  setSearchParameters,
}: {
  closeDialog: () => void;
  searchParameters: SearchParameters;
  setSearchParameters: (searchParameters: SearchParameters) => void;
}) => {
  const form = useForm({
    resolver: zodResolver(searchWithAJobSchema),
    defaultValues: {
      jobId: searchParameters.jobId,
      radius: searchParameters.radius,
      yoe: searchParameters.yoe,
    },
  });

  const jobs = jobsStore((state) => state.jobs);
  const activeJobs = jobs.filter(({ job }) => job.status === JobStatus.ACTIVE);

  const handleSubmit = (data: z.infer<typeof searchWithAJobSchema>) => {
    const selectedJob = activeJobs.find(({ job }) => job.id === data.jobId);

    setSearchParameters({
      jobId: data.jobId,
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      location: `${selectedJob?.job.city}, ${selectedJob?.job.state}`,
      query: selectedJob?.job.title ?? "",
      radius: data.radius,
      yoe: data.yoe,
      customFilter: [],
    });
    closeDialog();
  };

  return (
    <Form {...form}>
      <form
        className="space-y-4"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        {/* TODO: This should be an autocomplete combobox. However, that component is yet to be made. */}
        {/* Job */}
        <FormField
          control={form.control}
          name="jobId"
          render={({ field }) => (
            <FormItem className="text-slate-700">
              <FormLabel>Select a job</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a job" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="text-slate-700">
                  {activeJobs.map(({ job }) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <SearchWithAJobLocationSpecialInput />

        {/* Distance */}
        <FormField
          control={form.control}
          name="radius"
          render={({ field }) => {
            return (
              <FormItem className="text-slate-700">
                <FormLabel>Candidate distance</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select candidate distance" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="text-slate-700">
                    {searchCandidateDistanceOptions.map(
                      (candidateDistanceOption) => (
                        <SelectItem
                          key={candidateDistanceOption.value}
                          value={candidateDistanceOption.value}
                        >
                          {candidateDistanceOption.label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Years of Experience */}
        <FormField
          control={form.control}
          name="yoe"
          render={({ field }) => (
            <FormItem className="text-slate-700">
              <FormLabel>Years of experience</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select years of experience" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="text-slate-700">
                  {searchYearsOfExperienceOptions.map(
                    (yearsOfExperienceOption) => (
                      <SelectItem
                        key={yearsOfExperienceOption.value}
                        value={yearsOfExperienceOption.value}
                      >
                        {yearsOfExperienceOption.label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button
            className="rounded-full"
            onClick={closeDialog}
            type="button"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button className="rounded-full" type="submit">
            Start search
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export const SearchDialog = ({
  searchParameters,
  setSearchParameters,
}: {
  searchParameters: SearchParameters;
  setSearchParameters: (searchParameters: SearchParameters) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full" variant="outline">
          <SearchIcon />
          New search
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader
        // This empty `<DialogHeader />` is used to ensure
        // that the close button doesn't overlap with tabs.
        />
        <SearchWithAJobForm
          closeDialog={() => {
            setOpen(false);
          }}
          searchParameters={searchParameters}
          setSearchParameters={setSearchParameters}
        />
      </DialogContent>
    </Dialog>
  );
};
